import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import CompanyStore from 'stores/CompanyStore/store';
import { Roles } from 'constants/AdminConstants';
import { cn } from 'lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import useCoachKnockFeed from 'hooks/useCoachKnockFeed';
import useCoachPresencePing from 'hooks/useCoachPresencePing';
import { selectActiveFeedItems, isCheckinInteractive, isMorningCheckinItem, feedItemBodyHtml } from 'utils/KnockFeedUtils';
import sanitizeFeedHtml from 'utils/sanitizeFeedHtml';
import toNumberOrNull from 'utils/NumberUtils';
/**
 * Virtual Coach in-app notification feed (SCO-371). A Knock-backed bell + dropdown in
 * the coach header rendering un-interacted items; morning-checkin items get
 * Yes/No buttons that mark the message interacted with `{ action }` — which the
 * SCO-365 webhook resolves (first-response-wins). The sole notification surface
 * in the header (it replaced the legacy Flux NotificationsMenu, retired in
 * SCO-374); reads the current user from the store and stays empty + inert until
 * Knock enhanced security + the session keys are configured.
 */
var CoachKnockFeed = function (_a) {
    var _b, _c;
    var page = _a.page;
    var info = CompanyStore.getCompanyInfo();
    var userId = toNumberOrNull((_b = info === null || info === void 0 ? void 0 : info.current_user) === null || _b === void 0 ? void 0 : _b.id);
    var roleId = toNumberOrNull((_c = info === null || info === void 0 ? void 0 : info.current_user) === null || _c === void 0 ? void 0 : _c.role_id);
    // Producers don't receive the coach feed (morning-checkin etc. go to coaches),
    // so don't even open a Knock session/feed for them (passing null no-ops the hook).
    var isSalesperson = roleId === Roles.SALESPERSON;
    var _d = useCoachKnockFeed(isSalesperson ? null : userId), items = _d.items, markInteracted = _d.markInteracted;
    // Presence ping (SCO-469): while this coach surface is on screen, tell the
    // backend so morning-checkin pushes are skipped for a coach already looking.
    // Rides this component because it mounts on every coach header, independent
    // of whether the Knock feed itself has configured/loaded any items.
    useCoachPresencePing(!isSalesperson && userId !== null);
    // Bounded clock tick (Codex round 1, #407 P1): with no realtime event, a
    // render-captured `now` never crosses the 60-min interactivity boundary or
    // midnight — a card could stay live-looking indefinitely. 30s granularity
    // is plenty for hour/day boundaries and costs one state bump.
    var _e = useState(function () { return new Date(); }), now = _e[0], setNow = _e[1];
    useEffect(function () {
        var tick = setInterval(function () { return setNow(new Date()); }, 30000);
        return function () { return clearInterval(tick); };
    }, []);
    if (isSalesperson) {
        return null;
    }
    var active = selectActiveFeedItems(items, now);
    var isCoach = page === 'coach';
    var count = active.length;
    // Render nothing until there's an actual Virtual Coach item to show. Keeps the
    // surface fully dark pre-launch (the session endpoint 500s until Knock is
    // configured → empty feed), and avoids a redundant always-empty second bell.
    if (count === 0) {
        return null;
    }
    /*
     * Owner ruling 2 (2026-08-11): a coach-facing question carries no clock —
     * the backend sweep is gone and a human answer lands over a machine
     * timeout, so the buttons stay live for as long as the card is visible.
     * `isCheckinInteractive` is now always true; the inert branch survives as
     * the seam for any future visibility rule.
     */
    var renderCheckinActions = function (item) { return (isCheckinInteractive(item, now) ? (_jsxs("div", { className: "mt-3 flex gap-2", children: [_jsx("button", { type: "button", className: "rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90", onClick: function () { return markInteracted(item, { action: 'yes' }); }, children: "Yes" }), _jsx("button", { type: "button", className: "rounded-md border border-border px-3 py-1 text-xs font-semibold hover:bg-black/5", onClick: function () { return markInteracted(item, { action: 'no' }); }, children: "No" })] })) : (_jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: "This check-in has expired." }))); };
    var renderItem = function (item) { return (_jsxs("div", { className: "border-b border-border p-4 text-sm last:border-0", children: [_jsx("div", { className: "[&_p]:m-0", dangerouslySetInnerHTML: {
                    __html: sanitizeFeedHtml(feedItemBodyHtml(item.blocks), {
                        app: window.location.origin,
                        api: CompanyStore.getApiEndpoint()
                    })
                } }), isMorningCheckinItem(item) ? renderCheckinActions(item) : null] }, item.id)); };
    return (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsxs("button", { type: "button", "aria-label": "Virtual Coach notifications", className: cn('relative flex h-9 w-9 items-center justify-center rounded-md border-0 bg-transparent p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', isCoach ?
                        'text-white hover:bg-white/10' :
                        'text-primary hover:bg-black/5'), children: [_jsx(Bell, { className: "h-5 w-5" }), count ? (_jsx("span", { className: "absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground", children: count })) : null] }) }), _jsx(DropdownMenuContent, { align: "end", className: "z-[1000000] max-h-[70vh] w-80 overflow-y-auto p-0", children: _jsx("div", { className: "sb-knock-feed", children: active.map(renderItem) }) })] }));
};
export default CoachKnockFeed;
